<template>
  <header-block></header-block>
  <div class="page-container">
    <div class="inner-wide menu-grid">
      <div class="menu-side">
        <div class="white-box">
          <div class="side-head">
            <h2 class="section-label">{{ $t("editor.menus") }}</h2>
            <button
              class="text-btn"
              title="Create new menu"
              v-on:click="showAddNew = true"
            >
              + {{ $t("editor.new_menu") }}
            </button>
          </div>
          <div class="side-list">
            <button
              v-for="page in menus"
              :key="page.id"
              v-on:click="
                activeMenu = page;
                showAddNew = false;
              "
              :class="{ active: !showAddNew && activeMenu?.id === page.id }"
            >
              <span class="side-list-label">{{ page.name }}</span>
              <span class="side-list-count">{{ page.menu_items?.length }}</span>
            </button>
          </div>
        </div>
        <div class="white-box">
          <h2 class="section-label side-head">{{ $t("editor.languages") }}</h2>
          <div class="side-list">
            <button
              v-for="language in languages"
              :key="language.code"
              v-on:click="activeLanguage = language"
              :class="{ active: activeLanguage?.code === language.code }"
            >
              <span class="side-list-label">{{ language.name }}</span>
              <span class="side-list-count">{{
                language.code?.toUpperCase()
              }}</span>
            </button>
          </div>
        </div>
      </div>
      <div class="white-box menu-center" v-if="showAddNew">
        <div class="card-head">
          <h1>{{ $t("editor.add_new_menu") }}</h1>
        </div>
        <label class="field mb-2">
          <span class="field-label">{{ $t("editor.menu_name") }}</span>
          <input type="text" v-model="newMenu.name" />
        </label>
        <label class="field mb-3">
          <span class="field-label">{{ $t("editor.menu_id") }}</span>
          <input type="text" v-model="newMenu.id" />
        </label>
        <button class="btn btn-primary" v-on:click="addNewMenu">
          {{ $t("editor.add") }}
        </button>
      </div>
      <div class="white-box menu-center" v-else-if="activeMenu">
        <div class="card-head">
          <h1>{{ activeMenu.name }}</h1>
        </div>
        <div class="item-list">
          <draggable
            :list="activeMenu.menu_items"
            group="menus"
            filter=".filtered"
            animation="300"
            @change="save"
          >
            <template #item="{ element }">
              <div class="item menu-item-row">
                <svg
                  class="drag-handle"
                  viewBox="0 0 24 24"
                  width="14"
                  height="14"
                  fill="currentColor"
                >
                  <circle cx="9" cy="5" r="1.6" />
                  <circle cx="15" cy="5" r="1.6" />
                  <circle cx="9" cy="12" r="1.6" />
                  <circle cx="15" cy="12" r="1.6" />
                  <circle cx="9" cy="19" r="1.6" />
                  <circle cx="15" cy="19" r="1.6" />
                </svg>
                <span class="item-label">
                  {{
                    element.translations[activeLanguage.code] ??
                    firstTranslation(element)
                  }}
                </span>
                <span
                  class="badge-soft badge-yellow"
                  v-if="!element.translations[activeLanguage.code]"
                >
                  {{ firstTranslationCode(element)?.toUpperCase() }}
                </span>
                <span class="menu-url" v-if="element.target_url">
                  {{ element.target_url }}
                </span>
                <span class="menu-url" v-if="element.target_category_id">
                  /category/{{ element.target_category_id }}-{{ element.slug }}
                </span>
                <span class="menu-url" v-if="element.target_page_id">
                  /{{ element.target_page_id }}-{{ element.slug }}
                  <span class="badge-soft badge-blue" v-if="element.webshop_id > 0">
                    {{webshops[element.webshop_id]?.name}}
                  </span>
                </span>
                <button
                  class="icon-btn icon-btn-accent"
                  title="Edit link"
                  v-on:click="editItem(element)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path
                      d="M15.728 9.686l-1.414-1.414L5 17.586V19h1.414l9.314-9.314zm1.414-1.414l1.414-1.414-1.414-1.414-1.414 1.414 1.414 1.414zM7.242 21H3v-4.243L16.435 3.322a1 1 0 0 1 1.414 0l2.829 2.829a1 1 0 0 1 0 1.414L7.243 21z"
                    />
                  </svg>
                </button>
                <button
                  class="icon-btn icon-btn-danger"
                  title="Remove link"
                  v-on:click="deleteItem(element)"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    viewBox="0 0 24 24"
                    width="18"
                    height="18"
                  >
                    <path fill="none" d="M0 0h24v24H0z" />
                    <path
                      d="M17 6h5v2h-2v13a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V8H2V6h5V3a1 1 0 0 1 1-1h8a1 1 0 0 1 1 1v3zm1 2H6v12h12V8zm-9 3h2v6H9v-6zm4 0h2v6h-2v-6zM9 4v2h6V4H9z"
                    />
                  </svg>
                </button>
              </div>
            </template>
          </draggable>
        </div>
      </div>
      <div class="menu-side">
        <!-- One card per group. Stacked inside a single card the lists ran
             together — a heading alone stops separating them once a list is
             long enough to scroll past its own title. -->
        <div class="side-intro">
          <h2 class="section-label">Available items</h2>
          <div class="hint-text">Drag an item into the menu on the left.</div>
        </div>
        <div class="white-box">
          <h3 class="avail-title">Categories</h3>
          <div class="item-list avail-list">
            <draggable
              :list="possibleCategoryItems"
              :group="{ name: 'menus', pull: 'clone', put: false }"
              filter=".filtered"
              animation="300"
            >
              <template #item="{ element }">
                <div class="item avail-item">
                  <span class="item-label">{{
                    element.translations[activeLanguage.code]
                  }}</span>
                  <span class="menu-url">
                    /category/{{ element.target_category_id }}-{{
                      element.slug
                    }}
                  </span>
                </div>
              </template>
            </draggable>
          </div>
        </div>
        <div class="white-box">
          <h3 class="avail-title">Pages</h3>
          <div class="item-list avail-list">
            <draggable
              :list="possiblePageItems"
              :group="{ name: 'menus', pull: 'clone', put: false }"
              filter=".filtered"
              animation="300"
            >
              <template #item="{ element }">
                <div class="item avail-item">
                  <span class="item-label">{{
                    element.translations[activeLanguage.code]
                  }}</span>
                  <span class="menu-url">
                    /{{ element.target_category_id }}-{{ element.slug }}
                    <span class="badge-soft badge-blue" v-if="element.webshop_id > 0">
                      {{webshops[element.webshop_id]?.name}}
                    </span>
                  </span>
                </div>
              </template>
            </draggable>
          </div>
        </div>
        <div class="white-box">
          <div class="side-head">
            <h3 class="avail-title">Custom URL</h3>
            <button
              class="text-btn"
              title="Add custom link"
              v-on:click="addLinkShow = true"
            >
              + Link
            </button>
          </div>
          <div class="item-list avail-list">
            <draggable
              :list="possibleLinkItems"
              :group="{ name: 'menus', pull: 'clone', put: false }"
              filter=".filtered"
              animation="300"
            >
              <template #item="{ element }">
                <div class="item avail-item">
                  <span class="item-label">{{
                    element.translations[activeLanguage.code]
                  }}</span>
                  <span class="menu-url">{{ element.target_url }}</span>
                </div>
              </template>
            </draggable>
          </div>
        </div>
      </div>
    </div>
    <AddNewLink v-model:show="addLinkShow" v-on:addLink="addLink" />
    <EditMenuItem
      v-model:show="editItemShow"
      v-model:data="selectedItem"
      v-model:languages="languages"
      v-on:save="saveItem"
    />
  </div>
</template>
<script>
import HeaderBlock from "../components/header";
import http from "../modules/http";
import draggable from "vuedraggable";
import AddNewLink from "../components/modals/AddNewLink";
import EditMenuItem from "../components/modals/EditMenuItem";

export default {
  name: "MenuEditor",
  components: { EditMenuItem, AddNewLink, HeaderBlock, draggable },
  data() {
    return {
      menus: [],
      languages: [],
      activeLanguage: {},
      webshops: [],
      possibleCategoryItems: [],
      possibleLinkItems: [],
      possiblePageItems: [],
      activeMenu: {},
      showAddNew: false,
      words: [],
      selectedItem: {},
      newMenu: {
        name: "",
        id: "",
      },
      addLinkShow: false,
      editItemShow: false,
    };
  },
  mounted() {
    this.load();
  },
  watch: {
    activePage() {
      if (this.activeLanguage?.code !== undefined) {
        this.loadWords();
      }
    },
    activeLanguage() {
      if (this.activeMenu?.id !== undefined) {
        this.loadWords();
      }
    },
  },
  methods: {
    firstTranslation(element) {
      const keys = Object.keys(element.translations ?? {});
      return keys.length > 0 ? element.translations[keys[0]] : "";
    },
    firstTranslationCode(element) {
      const keys = Object.keys(element.translations ?? {});
      return keys.length > 0 ? keys[0] : "";
    },
    load() {
      let loaded = 0;
      if (this.webshops.length === 0) {
        http.fetch("/webshops").then((data) => {
          this.webshops = data;
        });
      }
      http.fetch("/page").then((data) => {
        for (const item of data.data) {
          item.target_page_id = item.id;
          delete item.id;
          item.translations = {};
          item.translations[this.activeLanguage.code] = item.name;
          this.possiblePageItems.push(item);
        }
        loaded++;
        if (loaded === 3) {
          this.loadMenu();
        }
      });
      http.fetch("/menu").then((data) => {
        this.menus = data.data;
        this.activeMenu = this.menus[0];
        loaded++;
        if (loaded === 3) {
          this.loadMenu();
        }
      });
      http.fetch("/languages").then((data) => {
        this.languages = data;
        this.activeLanguage = this.languages[0];

        http
          .fetch("/products/categories/products/" + this.activeLanguage.code)
          .then((data) => {
            this.addCategories(data["data"]);
            loaded++;
            if (loaded === 3) {
              this.loadMenu();
            }
          });
      });
    },
    loadMenu() {
      for (const menu of this.menus) {
        for (const item of menu.menu_items) {
          if (
            item.target_category_id !== null &&
            item.target_category_id !== undefined
          ) {
            let e = this.possibleCategoryItems.find(
              (e) => e.target_category_id === item.target_category_id
            );
            if (e !== undefined) {
              item.slug = e.slug;
            }
          }
          if (
            item.target_page_id !== null &&
            item.target_page_id !== undefined
          ) {
            let e = this.possiblePageItems.find(
              (e) => e.target_page_id === item.target_page_id
            );
            if (e !== undefined) {
              item.slug = e.slug;
            }
          }
          if (
            item.target_url !== null &&
            item.target_url !== undefined &&
            item.target_url.length > 0
          ) {
            let e = {
              translations: item.translations,
              target_url: item.target_url,
            };
            this.possibleLinkItems.push(e);
          }
        }
      }
    },
    addCategories(categories) {
      for (const item of categories) {
        item.target_category_id = item.id;
        delete item.id;
        item.translations = {};
        item.translations[this.activeLanguage.code] = item.name;
        this.possibleCategoryItems.push(item);
        if (item.categories !== undefined) {
          this.addCategories(item.categories);
        }
      }
    },
    loadWords() {
      this.possibleCategoryItems = [];
      http
        .fetch("/products/categories/products/" + this.activeLanguage.code)
        .then((data) => {
          this.addCategories(data["data"]);
        });
    },
    addNewMenu() {
      http.fetch("/menu", this.newMenu).then((data) => {
        this.menus.push(data.data);
        this.activeMenu = this.menus[this.menus.length - 1];
        this.showAddNew = false;
      });
    },
    addNew(newIndex, element) {
      let add = {
        order: newIndex,
      };
      add.translations = element.translations;
      if (element.target_category_id !== undefined) {
        add.target_category_id = element.target_category_id;
      }
      if (element.target_page_id !== undefined) {
        add.target_page_id = element.target_page_id;
      }
      if (element.target_url !== undefined) {
        add.target_url = element.target_url;
      }

      http.fetch(`/menus/${this.activeMenu.id}/menuitem`, add);
    },
    editItem(element) {
      this.selectedItem = element;
      this.editItemShow = true;
    },
    saveItem() {
      http.fetch(
        `/menus/${this.activeMenu.id}/menuitem/${this.selectedItem.id}`,
        this.selectedItem,
        false,
        "PUT"
      );
    },
    deleteItem(element) {
      let i = this.activeMenu.menu_items.findIndex((e) => e.id === element.id);
      this.activeMenu.menu_items.splice(i, 1);
      http.fetch(
        `/menus/${this.activeMenu.id}/menuitem/${element.id}`,
        {},
        false,
        "DELETE"
      );
    },
    order() {
      let order = [];
      let i = 0;
      for (const item of this.activeMenu.menu_items) {
        order.push({
          id: item.id,
          position: i++,
        });
      }
      http.fetch(
        `/menus/${this.activeMenu.id}/menuitem/order`,
        { data: order },
        false,
        "PUT"
      );
    },
    save(event) {
      if (event.added !== undefined) {
        this.addNew(event.added.newIndex, event.added.element);
      }
      if (event.moved !== undefined) {
        this.order();
      }
    },
    addLink(link) {
      let item = { translations: {}, target_url: link.link };
      item.translations[this.activeLanguage.code] = link.name;
      this.possibleLinkItems.push(item);
    },
  },
};
</script>
<style scoped>
.menu-grid {
  display: grid;
  grid-template-columns: 210px 1fr 300px;
  gap: 14px;
  align-items: start;
}
@media (max-width: 1100px) {
  .menu-grid {
    grid-template-columns: 1fr;
  }
}
.menu-side {
  display: flex;
  flex-direction: column;
  gap: 14px;
  min-width: 0;
}
.menu-side .white-box {
  margin-bottom: 0;
}
.side-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 6px;
  padding: 0 6px;
}
.side-list {
  display: flex;
  flex-direction: column;
  gap: 2px;
}
.side-list button {
  border: none;
  cursor: pointer;
  padding: 8px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 700;
  background: transparent;
  color: #505354;
  display: flex;
  align-items: center;
  gap: 8px;
  text-align: left;
  width: 100%;
  transition: background 0.15s;
}
.side-list button:hover {
  background: #f1f5f9;
}
.side-list button.active {
  background: rgba(124, 58, 237, 0.12);
  color: #5b21b6;
}
.side-list-label {
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
.side-list-count {
  font-size: 11px;
  color: #768692;
  font-weight: 600;
}
.menu-center {
  min-width: 0;
  margin-bottom: 0;
}
.menu-item-row {
  cursor: grab;
}
.menu-item-row .drag-handle {
  flex: none;
  color: #c0bdbd;
}
.item-label {
  font-size: 13px;
  font-weight: 700;
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}
/* the card edge now carries the separation, so the heading no longer needs
   space above it to stand apart from the list before it */
.side-intro {
  padding: 0 4px;
}
.side-intro .hint-text {
  margin-top: 2px;
}
.avail-title {
  font-size: 12px;
  font-weight: 700;
  margin: 0 6px 8px 6px;
}
.avail-list {
  max-height: 200px;
  overflow-y: auto;
}
.avail-item {
  cursor: grab;
  padding: 8px 10px;
}
</style>
